import {
  ChangeDetectorRef,
  ViewEncapsulation,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DocumentModel } from '@data-layer/models/documents';
import { ALLOWED_FILE_EXTENSIONS } from '@constants/constants';
import { enumFileExtension } from '@enum/enumFileExtension';
import { CustomValidator } from '@helpers/custom-validator';
import { UntypedFormControl } from '@angular/forms';
import { environment } from '@env/environment';
import { GenericService } from '@services/generic.service';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { Observable } from 'rxjs';

@Component({
  selector: 'document-upload-image',
  templateUrl: './document-upload-image.component.html',
  styleUrls: ['./document-upload-image.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DocumentUploadImageComponent implements OnInit, OnChanges {
  @Input() public downloadLink: string | ArrayBuffer;
  @Input() public isLoading: boolean;
  @Input() public footerHint: string = 'LABELS.DRAG_N_DROP_FOR_UPLOAD_BROWSE_HIGHLIGHTED';
  @Input() public isShowLoader: boolean = true;
  @Input() public headerLabel: string = 'LABELS.COMPANY_LOGO';
  @Input() public badgeLabel: string = 'LABELS.OPTIONAL';
  @Input() public badgeClass: string = '';
  @Input() public descriptionHint: string = 'COMPANY.UPLOAD_YOUR_LOGO_HINT';
  @Input() public uploadedFileLabel: string = 'LABELS.COMPANY_LOGO';
  @Input() public showRemoveButton: boolean = true;
  @Input() public isNonImageDocument: boolean = false;
  @Input() public fileName: string = '';
  // #13269 — unique file-input id per instance; multiple instances on one page collide on the default id.
  @Input() public inputId: string = 'logo-input';
  // #13356 — the card was built image-only (logo); document uploads (SS-4) need PDF too. Both inputs
  // default to the historical image-only behavior, so existing logo/photo-id usages are unchanged.
  @Input() public acceptAttribute: string = 'image/*';
  @Input() public allowDocumentFiles: boolean = false;
  // The native accept attribute only filters the Browse dialog — drag-and-drop bypasses it, so the
  // whitelist below is what actually rejects a file. Hosts needing a narrower set override both.
  @Input() public allowedExtensions: enumFileExtension[] = ALLOWED_FILE_EXTENSIONS;
  @Input() public extensionErrorKey: string = 'FORM_ERROR.FILE_EXTENSION_NOT_ALLOWED';
  @Input() public nonImageIconClass: string = 'fa-user';
  @Output() public upload: EventEmitter<File> = new EventEmitter<File>();
  @Output() public remove: EventEmitter<DocumentModel> = new EventEmitter<DocumentModel>();

  @ViewChild('inputElement', { static: false }) public inputElement: ElementRef;
  @ViewChild('replaceInputElement', { static: false }) public replaceInputElement: ElementRef;

  public fileControl: UntypedFormControl = new UntypedFormControl(null, [
    CustomValidator.maxFileSize(environment.files.default_file_size_limit),
    CustomValidator.onlyImage,
  ]);
  public errors: IErrorForm = { maxFileSize: 'FORM_ERROR.MAX_FILE_SIZE', onlyImage: 'FORM_ERROR.LOGO_FILE_TYPE' };
  public isHoverFile: boolean = false;
  public isNonImageFile: boolean = false;
  public uploadedFileName: string = '';
  public isTablet$: Observable<boolean>;

  public isUploadingDoc: boolean;

  public get isNonImage(): boolean {
    return this.isNonImageFile || this.isNonImageDocument;
  }

  public get displayFileName(): string {
    return this.uploadedFileName || this.fileName || '';
  }

  public get isDownloadableUrl(): boolean {
    return typeof this.downloadLink === 'string' && this.downloadLink.startsWith('http');
  }

  public get replaceInputId(): string {
    return `${this.inputId}-replace`;
  }

  constructor(private cdr: ChangeDetectorRef, private genericService: GenericService) {
    this.isTablet$ = this.genericService.isTablet$;
  }

  // #13356 — swap the image-only rule for the project-wide extension allow-list when the host uploads
  // documents. The control instance is kept so the bound app-control-errors keeps working.
  public ngOnInit(): void {
    if (!this.allowDocumentFiles) {
      return;
    }
    this.fileControl.setValidators([
      CustomValidator.maxFileSize(environment.files.default_file_size_limit),
      CustomValidator.fileExtensions(this.allowedExtensions),
    ]);
    this.errors = { ...this.errors, fileExtensionNotAllowed: this.extensionErrorKey };
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!!changes.downloadLink && this.isUploadingDoc) {
      this.isUploadingDoc = false;
    }
  }

  public handleFiles(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const files: FileList = target.files;
    if (files && files.length) {
      if (this.isShowLoader) {
        this.isUploadingDoc = true;
      }
      this.uploadFile(files[0]);
    }
  }

  public removeFile(event: Event): void {
    this.downloadLink = null;
    event.stopPropagation();
    event.preventDefault();
    this.remove.emit();
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.effectAllowed === 'copyLink') {
      this.isHoverFile = true;
    }
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.files && event.dataTransfer.files.length) {
      if (this.isShowLoader) {
        this.isUploadingDoc = true;
      }
      this.uploadFile(event.dataTransfer.files[0]);
      this.isHoverFile = false;
    }
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.effectAllowed === 'copyLink') {
      this.isHoverFile = false;
    }
  }

  public clickInput() {
    this.clearFile();
  }

  public uploadFile(file: File): void {
    if (file) {
      const reader: FileReader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event: ProgressEvent<FileReader>) => {
        this.isNonImageFile = !file.type.startsWith('image/');
        this.downloadLink = this.isNonImageFile ? 'non-image' : reader.result;
        this.clearFile();
        this.fileControl.patchValue(file);
        this.fileControl.markAsTouched();

        if (this.inputElement) {
          this.inputElement.nativeElement.value = null;
        } else if (this.replaceInputElement) {
          this.replaceInputElement.nativeElement.value = null;
        }
        if (this.fileControl.valid) {
          this.uploadedFileName = file.name;
          this.upload.emit(file);
        } else {
          this.isUploadingDoc = false;
        }
        this.cdr.detectChanges();
      };
    }
  }

  public downloadFile(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (!this.isDownloadableUrl) {
      return;
    }
    const url = this.downloadLink as string;
    const filename = this.extractFilename(url);
    fetch(url, { cache: 'no-store' })
      .then((res) => {
        const contentType = res.headers.get('Content-Type') || '';
        const finalFilename = this.ensureExtension(filename, contentType);
        return res.blob().then((blob) => ({ blob, finalFilename }));
      })
      .then(({ blob, finalFilename }) => {
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = finalFilename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {
        window.open(url, '_blank');
      });
  }

  public handleReplaceFiles(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const files: FileList = target.files;
    if (files && files.length) {
      this.uploadFile(files[0]);
    }
  }

  public clickReplaceInput(): void {
    this.clearFile();
  }

  private ensureExtension(filename: string, contentType: string): string {
    if (filename.includes('.')) {
      return filename;
    }
    const mimeToExt: Record<string, string> = {
      'image/jpeg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/webp': '.webp',
      'image/svg+xml': '.svg',
      'application/pdf': '.pdf',
    };
    const ext = mimeToExt[contentType.split(';')[0].trim()] || '';
    return filename + ext;
  }

  private extractFilename(url: string): string {
    try {
      const parsed = new URL(url);
      const rscd = parsed.searchParams.get('rscd') || '';
      const filenameMatch = rscd.match(/filename="?([^"]+)"?/);
      if (filenameMatch) {
        return filenameMatch[1];
      }
      return parsed.pathname.split('/').pop() || 'download';
    } catch {
      return 'download';
    }
  }

  private clearFile(): void {
    this.fileControl.patchValue(null);
    this.fileControl.markAsUntouched();
  }
}
